import React from 'react'
import { Link } from 'react-router-dom'
import LogoTransparent from '../Pictures/logo_low.png'
import bannerPic1 from '../Pictures/banner1.jpg'
import { Events } from '../Data/Events'
import { News } from '../Data/News'
import { Partners } from '../Data/Partners'
import { Zoom } from 'react-awesome-reveal'
import { Container, Icon } from 'semantic-ui-react'
import { Carousel } from 'antd'
import { useMediaQuery } from 'react-responsive'

const Home = () => {
  const bannerStyle = {
    backgroundImage: `url(${bannerPic1})`,
  }

  const mobile = useMediaQuery({ query: '(max-width:1150px)' })

  const upcomingEvents = [...Events]
    .filter(e => e.sort && new Date(e.sort) >= new Date())
    .sort((a, b) => new Date(a.sort) - new Date(b.sort))
    .slice(0, 2)

  // If no future events, take the 2 most recent
  const displayEvents =
    upcomingEvents.length > 0
      ? upcomingEvents
      : [...Events].slice(-2).reverse()

  const latestNews = [...News].slice(0, 3)

  return (
    <div id='home'>
      {/* Hero Banner */}
      <section id='banner' style={bannerStyle}>
        <div id='bannerOverlay'></div>
        <div id='bannerText'>
          <Zoom
            triggerOnce
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <div id='summary'>
              <img
                alt='Ghana Union Emblem'
                src={LogoTransparent}
                className='bannerPic'
              />
              <h1 className='bannerHeading'>GHANA UNION CZECH REPUBLIC</h1>
            </div>
            <div className='bannerCtaGroup'>
              <Link to='/joinus' className='bannerPrimaryBtn'>
                Join Us Today
              </Link>
              <Link to='/about' className='bannerSecondaryBtn'>
                About Us
              </Link>
            </div>
          </Zoom>
        </div>
      </section>

      {/* Editorial Who We Are & What We Do */}
      <section className='homeSection homeIntroSection'>
        <Container>
          <div className='homeIntroGrid'>
            <div className='homeIntroBlock'>
              <span className='sectionBadge'>Who We Are</span>
              <h2 className='homeSectionTitle'>About the Union</h2>
              <div className='goldAccentLine'></div>
              <p className='homeIntroText'>
                The Ghana Union Czech Republic is a registered, welfare, non-profit,
                and non-governmental association. We bring together Ghanaians and
                friends of Ghana across the Czech Republic to support one another,
                celebrate our culture, and foster smooth integration into Czech society.
              </p>
              <Link to='/about' className='homeTextLink'>
                Read our story <Icon name='arrow right' />
              </Link>
            </div>

            <div className='homeIntroBlock'>
              <span className='sectionBadge'>What We Do</span>
              <h2 className='homeSectionTitle'>Our Mission & Support</h2>
              <div className='goldAccentLine'></div>
              <ul className='homeServicesList'>
                <li>
                  <Icon name='check circle' />
                  <span>
                    Welfare assistance for members during key life milestones and moments of need.
                  </span>
                </li>
                <li>
                  <Icon name='check circle' />
                  <span>
                    Endorsement and witnessing of Ghana passport applications and renewals.
                  </span>
                </li>
                <li>
                  <Icon name='check circle' />
                  <span>
                    Educational, cultural, social, and healthcare initiatives throughout the year.
                  </span>
                </li>
                <li>
                  <Icon name='check circle' />
                  <span>
                    Active liaison between the Ghanaian community and the Ghana Embassy.
                  </span>
                </li>
              </ul>
              <Link to='/joinus' className='homeTextLink'>
                Explore membership benefits <Icon name='arrow right' />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Upcoming Events Section */}
      <section className='homeSection homeEventsSection'>
        <Container>
          <div className='sectionHeaderRow'>
            <div>
              <span className='sectionBadge'>Calendar</span>
              <h2 className='homeSectionTitle'>Upcoming Events</h2>
              <div className='goldAccentLine'></div>
            </div>
            <Link to='/events' className='sectionViewAllLink'>
              View All Events <Icon name='arrow right' />
            </Link>
          </div>

          {displayEvents.length === 0 ? (
            <div className='homeEmptyState'>
              <h3>No upcoming events scheduled at this moment.</h3>
              <p>Check back soon or explore past event galleries.</p>
              <Link to='/events' className='bannerPrimaryBtn'>
                View Events Calendar
              </Link>
            </div>
          ) : (
            <div className='homeGrid'>
              {displayEvents.map(event => (
                <article className='homeGridItem' key={event.id}>
                  <Link to={`/eventdetails/${event.id}`} className='homeItemLink'>
                    {event.image && (
                      <div className='homeItemImageWrapper'>
                        <img
                          src={event.image}
                          alt={event.event}
                          className='homeItemImage'
                          loading='lazy'
                        />
                      </div>
                    )}
                    <div className='homeItemBody'>
                      <div className='homeItemMeta'>
                        {event.date && (
                          <span className='homeMetaTag'>
                            <Icon name='calendar alternate outline' /> {event.date}
                          </span>
                        )}
                        {event.location && event.location !== 'TBA' && (
                          <span className='homeMetaTag'>
                            <Icon name='map marker alternate' /> {event.location}
                          </span>
                        )}
                      </div>
                      <h3 className='homeItemHeadline'>{event.event}</h3>
                      {event.summary && (
                        <p className='homeItemExcerpt'>
                          {typeof event.summary === 'string'
                            ? event.summary
                            : 'Click to view full event information and location.'}
                        </p>
                      )}
                      <span className='homeItemActionLink'>
                        Event Details <Icon name='arrow right' />
                      </span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Latest News Section */}
      <section className='homeSection homeNewsSection'>
        <Container>
          <div className='sectionHeaderRow'>
            <div>
              <span className='sectionBadge'>Updates</span>
              <h2 className='homeSectionTitle'>Latest News</h2>
              <div className='goldAccentLine'></div>
            </div>
            <Link to='/news' className='sectionViewAllLink'>
              View All News <Icon name='arrow right' />
            </Link>
          </div>

          <div className='homeGrid'>
            {latestNews.map(item => (
              <article className='homeGridItem' key={item.id}>
                <Link to={`/newsdetails/${item.id}`} className='homeItemLink'>
                  {item.image && (
                    <div className='homeItemImageWrapper'>
                      <img
                        src={item.image}
                        alt={item.headline}
                        className='homeItemImage'
                        loading='lazy'
                      />
                    </div>
                  )}
                  <div className='homeItemBody'>
                    <div className='homeItemMeta'>
                      {item.date && (
                        <span className='homeMetaTag'>
                          <Icon name='calendar alternate outline' /> {item.date}
                        </span>
                      )}
                    </div>
                    <h3 className='homeItemHeadline'>{item.headline}</h3>
                    {item.short && (
                      <p className='homeItemExcerpt'>{item.short}</p>
                    )}
                    <span className='homeItemActionLink'>
                      Read Article <Icon name='arrow right' />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Partners Section */}
      <section id='partners' className='homeSection'>
        <Container>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <span className='sectionBadge'>Collaborations</span>
            <h2 className='homeSectionTitle' style={{ textAlign: 'center' }}>
              Our Partners
            </h2>
            <div className='goldAccentLine' style={{ margin: '0 auto' }}></div>
          </div>

          <Carousel
            autoplay
            arrows={!mobile}
            dots={true}
            autoplaySpeed={8000}
            className='homePartnersCarousel'
          >
            {Partners.map(p => (
              <div className='carouselPartnerSlide' key={p.id}>
                {p.thumb && (
                  <img
                    src={p.thumb}
                    alt={p.name}
                    className='partnerSlideImg'
                  />
                )}
                <h3 className='partnerSlideTitle'>{p.name}</h3>
                <p className='partnerSlideDesc'>{p.description}</p>
                {p.web && (
                  <a
                    href={p.web}
                    target='_blank'
                    rel='noreferrer'
                    className='partnerPrimaryBtn'
                  >
                    Visit Website <Icon name='external alternate' />
                  </a>
                )}
              </div>
            ))}
          </Carousel>
        </Container>
      </section>
    </div>
  )
}

export default Home
